/**
 * Auto-map: pure, deterministic fuzzy matcher that proposes
 * `{ kind: "source", column }` rules for Shopify target columns whose name
 * resembles a source-workbook header.
 *
 * Used by the mapper sidebar's one-click "Auto-map" button. The function does
 * NOT decide whether to overwrite existing rules — that's the caller's
 * responsibility. We return EVERY match we found above the score threshold;
 * the page-level handler skips matches for already-set columns by default.
 *
 * Algorithm:
 *  1. Hard-coded overrides for Monjolika's quirky source headers (e.g. the
 *     "Product Tittle" typo) short-circuit the fuzzy pass with score 1.0.
 *  2. For every other Shopify column, normalize → score every source header.
 *     Normalization: lowercase, strip non-alphanumerics, drop generic stop
 *     words ("product", "the", "metafield", "metafields", "custom",
 *     "shopify"). Stop words are dropped at the *token* level, not as a
 *     substring trim — "ProductCategory" still scores against "Product
 *     Category" via the substring rule.
 *  3. Scoring (best wins):
 *       - Equal normalized strings → 1.0
 *       - One contains the other → 0.7
 *       - Token-set Jaccard ≥ 0.5 → jaccard
 *  4. A source header can be claimed by at most one Shopify column unless the
 *     match is exact (>0.95) — e.g. Monjolika reuses "Sku Code" for both
 *     Variant SKU and Variant Barcode.
 *  5. Matches with score < 0.6 are dropped.
 */

import type { Rule } from "./types";

export type AutoMapMatch = {
  column: string;
  sourceHeader: string;
  score: number;
};

export type AutoMapResult = {
  added: Record<string, Rule>;
  matched: AutoMapMatch[];
};

// ---------------------------------------------------------------------------
// Hard-coded overrides
// ---------------------------------------------------------------------------

/**
 * Exact column → source-header overrides. These win over fuzzy matching with
 * a score of 1.0 (i.e. they're treated like an exact match — including the
 * "shared source header" rule). Each entry is an exact Shopify column name.
 */
const EXACT_OVERRIDES: Record<string, string> = {
  Title: "Product Tittle",
  "Variant SKU": "Sku Code",
  "Variant Price": "Transfer Price",
  "Variant Compare At Price": "MRP",
  "Variant Grams": "Weight",
  "Variant Inventory Qty": "Inventory",
  "Image Alt Text": "Product Tittle",
};

/**
 * Substring-based overrides — fire when the Shopify column name contains the
 * key (case-insensitive). Map to a source header that should be matched at
 * score 1.0. Order is insignificant; the first match wins.
 */
const SUBSTRING_OVERRIDES: { needle: string; sourceHeader: string }[] = [
  { needle: "color-pattern", sourceHeader: "COLOR" },
  { needle: "fabric", sourceHeader: "FABRIC" },
  { needle: "pack_contains", sourceHeader: "Pack Contains" },
];

// ---------------------------------------------------------------------------
// Normalization + scoring
// ---------------------------------------------------------------------------

const STOP_WORDS = new Set([
  "product",
  "the",
  "metafield",
  "metafields",
  "custom",
  "shopify",
]);

/** Lowercase, drop non-alphanumerics. Cheap fingerprint. */
function normalize(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "");
}

/**
 * Token-set fingerprint. Splits on non-alphanumeric, lowercases, drops stop
 * words and short (<2) tokens. Used for the Jaccard score.
 */
function tokens(s: string): Set<string> {
  const out = new Set<string>();
  for (const raw of s.toLowerCase().split(/[^a-z0-9]+/)) {
    if (!raw || raw.length < 2) continue;
    if (STOP_WORDS.has(raw)) continue;
    out.add(raw);
  }
  return out;
}

function jaccard(a: Set<string>, b: Set<string>): number {
  if (a.size === 0 || b.size === 0) return 0;
  let inter = 0;
  for (const t of a) if (b.has(t)) inter++;
  const union = a.size + b.size - inter;
  return union === 0 ? 0 : inter / union;
}

/** Best similarity score for a (Shopify column, source header) pair. */
function scorePair(col: string, src: string): number {
  const nc = normalize(col);
  const ns = normalize(src);
  if (!nc || !ns) return 0;
  if (nc === ns) return 1;

  if (nc.includes(ns) || ns.includes(nc)) {
    return 0.7;
  }

  const j = jaccard(tokens(col), tokens(src));
  if (j >= 0.5) return j;
  return 0;
}

// ---------------------------------------------------------------------------
// Public entry point
// ---------------------------------------------------------------------------

const SCORE_THRESHOLD = 0.6;
const SHARED_SOURCE_THRESHOLD = 0.95;

export function autoMapHeaders(
  shopifyColumns: readonly string[],
  sourceHeaders: string[],
): AutoMapResult {
  const matched: AutoMapMatch[] = [];
  // source header (normalized) → number of times claimed
  const claimedNorm = new Map<string, number>();
  const sourceByNorm = new Map<string, string>();
  for (const h of sourceHeaders) {
    const n = normalize(h);
    if (n && !sourceByNorm.has(n)) sourceByNorm.set(n, h);
  }

  for (const col of shopifyColumns) {
    // 1. Exact-name overrides
    let best: { source: string; score: number } | null = null;

    const exact = EXACT_OVERRIDES[col];
    if (exact && sourceHeaders.includes(exact)) {
      best = { source: exact, score: 1 };
    }

    // 2. Substring overrides (only if no exact override matched)
    if (!best) {
      const colLower = col.toLowerCase();
      for (const ov of SUBSTRING_OVERRIDES) {
        if (colLower.includes(ov.needle) && sourceHeaders.includes(ov.sourceHeader)) {
          best = { source: ov.sourceHeader, score: 1 };
          break;
        }
      }
    }

    // 3. Fuzzy fallback
    if (!best) {
      for (const src of sourceHeaders) {
        const s = scorePair(col, src);
        if (s >= SCORE_THRESHOLD && (!best || s > best.score)) {
          best = { source: src, score: s };
        }
      }
    }

    if (!best) continue;

    // 4. Shared-source guard
    const srcNorm = normalize(best.source);
    const claims = claimedNorm.get(srcNorm) ?? 0;
    if (claims > 0 && best.score <= SHARED_SOURCE_THRESHOLD) {
      continue;
    }
    claimedNorm.set(srcNorm, claims + 1);

    matched.push({ column: col, sourceHeader: best.source, score: best.score });
  }

  const added: Record<string, Rule> = {};
  for (const m of matched) {
    added[m.column] = { kind: "source", column: m.sourceHeader };
  }
  return { added, matched };
}
